import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppStore } from '../stores/app'
import {
  listVisualCollections,
  createVisualCollection,
  updateVisualCollection,
  deleteVisualCollection,
  getVisualCollection,
  addTaskToCollection,
  removeTaskFromCollection,
  type VisualCollectionCreateRequest,
  type VisualCollectionUpdateRequest,
} from '../lib/api'
import { getErrorMessage } from '../lib/errors'
import { toast } from 'sonner'

export function useVisualCollections() {
  const organizationId = useAppStore((state) => state.organizationId)
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['visual_collections', organizationId],
    queryFn: async () => {
      if (!organizationId) return []
      return await listVisualCollections()
    },
    enabled: !!organizationId,
  })

  const createMutation = useMutation({
    mutationFn: async (data: VisualCollectionCreateRequest) => {
      return await createVisualCollection(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visual_collections', organizationId] })
      toast.success('Coleccion creada')
    },
    onError: (error) => {
      toast.error('Error al crear coleccion', { description: getErrorMessage(error) })
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: VisualCollectionUpdateRequest }) => {
      return await updateVisualCollection(id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visual_collections', organizationId] })
      toast.success('Coleccion actualizada')
    },
    onError: (error) => {
      toast.error('Error al actualizar coleccion', { description: getErrorMessage(error) })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteVisualCollection(id)
      return id
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visual_collections', organizationId] })
      toast.success('Coleccion eliminada')
    },
    onError: (error) => {
      toast.error('Error al eliminar coleccion', { description: getErrorMessage(error) })
    },
  })

  return {
    collections: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    createCollection: createMutation.mutateAsync,
    updateCollection: updateMutation.mutateAsync,
    deleteCollection: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  }
}

export function useVisualCollectionDetail(collectionId: string | null) {
  return useQuery({
    queryKey: ['visual_collection', collectionId],
    queryFn: async () => {
      if (!collectionId) return null
      return await getVisualCollection(collectionId)
    },
    enabled: !!collectionId,
  })
}

export function useCollectionMembership() {
  const organizationId = useAppStore((state) => state.organizationId)
  const queryClient = useQueryClient()

  const addMutation = useMutation({
    mutationFn: async ({
      collectionId,
      taskId,
      sortOrder = 0,
    }: {
      collectionId: string
      taskId: string
      sortOrder?: number
    }) => {
      return await addTaskToCollection(collectionId, taskId, sortOrder)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['visual_collection', variables.collectionId] })
      queryClient.invalidateQueries({ queryKey: ['visual_collections', organizationId] })
      toast.success('Tarea agregada a la coleccion')
    },
    onError: (error) => {
      toast.error('Error al agregar a la coleccion', { description: getErrorMessage(error) })
    },
  })

  const removeMutation = useMutation({
    mutationFn: async ({
      collectionId,
      taskId,
    }: {
      collectionId: string
      taskId: string
    }) => {
      await removeTaskFromCollection(collectionId, taskId)
      return { collectionId, taskId }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['visual_collection', variables.collectionId] })
      queryClient.invalidateQueries({ queryKey: ['visual_collections', organizationId] })
      toast.success('Tarea removida de la coleccion')
    },
    onError: (error) => {
      toast.error('Error al remover de la coleccion', { description: getErrorMessage(error) })
    },
  })

  return {
    addToCollection: addMutation.mutateAsync,
    removeFromCollection: removeMutation.mutateAsync,
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending,
  }
}
